import { useCallback, useEffect, useMemo, useState } from "react";
import {
  deleteTemplate,
  fetchTemplates,
  saveTemplateDraft,
  seedWebinarTemplates,
  submitTemplate,
  syncTemplates,
  type TemplateButton,
  type WaTemplate,
} from "@/services/waAgent";
import { btnDanger, btnGhost, btnPrimary, cardCls, inputCls } from "./ui";

const STATUS: Record<string, { label: string; cls: string }> = {
  DRAFT: { label: "Borrador", cls: "bg-background-200 text-foreground-700" },
  PENDING: { label: "En revisión", cls: "bg-amber-500/15 text-amber-700" },
  IN_APPEAL: { label: "En apelación", cls: "bg-amber-500/15 text-amber-700" },
  APPROVED: { label: "Aprobada", cls: "bg-primary-500/15 text-primary-700" },
  REJECTED: { label: "Rechazada", cls: "bg-accent-500/15 text-accent-700" },
  PAUSED: { label: "Pausada", cls: "bg-amber-500/15 text-amber-700" },
  DISABLED: { label: "Desactivada", cls: "bg-accent-500/15 text-accent-700" },
};
const CATEGORY: Record<string, string> = { UTILITY: "Utilidad", MARKETING: "Marketing", AUTHENTICATION: "Autenticación" };

type Draft = {
  id?: string;
  name: string;
  language: string;
  category: WaTemplate["category"];
  headerText: string;
  body: string;
  footer: string;
  buttons: TemplateButton[];
  examples: string[];
};

const EMPTY: Draft = { name: "", language: "es", category: "UTILITY", headerText: "", body: "", footer: "", buttons: [], examples: [] };

const varCount = (t: string) => new Set([...t.matchAll(/\{\{(\d+)\}\}/g)].map((m) => m[1])).size;

function validate(d: Draft): string | null {
  if (!/^[a-z0-9_]{3,100}$/.test(d.name)) return "El nombre: solo minúsculas, números y guiones bajos (mín. 3).";
  const b = d.body.trim();
  if (b.length < 10) return "Escribe el cuerpo del mensaje.";
  if (/^\{\{/.test(b) || /\}\}$/.test(b)) return "Meta no permite que el cuerpo empiece ni termine con una variable.";
  const n = varCount(b);
  for (let i = 1; i <= n; i++) if (!b.includes(`{{${i}}}`)) return "Las variables deben ser {{1}}, {{2}}… sin saltos.";
  if (n > 0 && d.examples.slice(0, n).filter((e) => e.trim()).length < n) return `Escribe un ejemplo para cada una de las ${n} variables.`;
  if (/\{\{/.test(d.headerText)) return "El encabezado no admite variables.";
  return null;
}

function Preview({ d }: { d: Draft }) {
  const n = varCount(d.body);
  let body = d.body;
  for (let i = 1; i <= n; i++) body = body.replaceAll(`{{${i}}}`, d.examples[i - 1]?.trim() || `{{${i}}}`);
  return (
    <div className="rounded-2xl bg-[#e7f5e1] dark:bg-background-200 border border-background-200 p-3 text-sm text-foreground-900 max-w-sm whitespace-pre-wrap">
      {d.headerText ? <div className="font-bold mb-1">{d.headerText}</div> : null}
      {body || <span className="text-foreground-400">Vista previa del mensaje…</span>}
      {d.footer ? <div className="text-[11px] text-foreground-500 mt-2">{d.footer}</div> : null}
      {d.buttons.length ? (
        <div className="mt-2 space-y-1">
          {d.buttons.map((b, i) => (
            <div key={i} className="text-center text-primary-700 font-semibold border-t border-background-300 pt-1">
              {b.text || "Botón"}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Editor({ initial, onClose, notify }: { initial: Draft; onClose: (saved: boolean) => void; notify: (m: string) => void }) {
  const [d, setD] = useState<Draft>(initial);
  const [busy, setBusy] = useState(false);
  const n = varCount(d.body);
  const err = validate(d);

  const save = async (andSubmit: boolean) => {
    setBusy(true);
    try {
      await saveTemplateDraft({ id: d.id, name: d.name, language: d.language, category: d.category, headerText: d.headerText, body: d.body, footer: d.footer, buttons: d.buttons, examples: d.examples.slice(0, n) });
      if (andSubmit) {
        const list = await fetchTemplates();
        const row = list.find((t) => t.name === d.name && t.language === d.language);
        if (row) {
          const r = await submitTemplate(row.id);
          notify(r.ok ? "Enviada a revisión de Meta" : r.error ?? "Meta la rechazó");
        }
      } else notify("Borrador guardado");
      onClose(true);
    } catch {
      notify("No se pudo guardar (¿ya existe una con ese nombre e idioma?)");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${cardCls} space-y-4`}>
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-foreground-950">{d.id ? "Editar plantilla" : "Nueva plantilla"}</h3>
        <button className="text-foreground-500 hover:text-foreground-900 cursor-pointer" onClick={() => onClose(false)} aria-label="Cerrar">
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>
      <div className="grid md:grid-cols-[1fr_auto] gap-5">
        <div className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-1">
              <span className="text-xs text-foreground-500">Nombre (minúsculas_y_guiones)</span>
              <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} className={`${inputCls} mt-1`} />
            </label>
            <label className="block">
              <span className="text-xs text-foreground-500">Idioma</span>
              <select value={d.language} onChange={(e) => setD({ ...d, language: e.target.value })} className={`${inputCls} mt-1`}>
                <option value="es">Español</option>
                <option value="es_MX">Español (México)</option>
                <option value="es_ES">Español (España)</option>
                <option value="en_US">Inglés (EE. UU.)</option>
                <option value="pt_BR">Portugués (Brasil)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-foreground-500">Categoría</span>
              <select value={d.category} onChange={(e) => setD({ ...d, category: e.target.value as Draft["category"] })} className={`${inputCls} mt-1`}>
                <option value="UTILITY">Utilidad (confirmaciones, avisos)</option>
                <option value="MARKETING">Marketing (promoción, recuperar)</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-foreground-500">Encabezado (opcional, sin variables)</span>
            <input maxLength={60} value={d.headerText} onChange={(e) => setD({ ...d, headerText: e.target.value })} className={`${inputCls} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs text-foreground-500">Cuerpo · usa {"{{1}}"}, {"{{2}}"}… para datos variables (no al inicio ni al final)</span>
            <textarea rows={5} maxLength={1024} value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} className={`${inputCls} mt-1 resize-y`} />
          </label>
          {n > 0 ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {Array.from({ length: n }, (_, i) => (
                <label key={i} className="block">
                  <span className="text-xs text-foreground-500">Ejemplo de {`{{${i + 1}}}`}</span>
                  <input
                    value={d.examples[i] ?? ""}
                    onChange={(e) => {
                      const ex = [...d.examples];
                      ex[i] = e.target.value;
                      setD({ ...d, examples: ex });
                    }}
                    className={`${inputCls} mt-1`}
                  />
                </label>
              ))}
            </div>
          ) : null}
          <label className="block">
            <span className="text-xs text-foreground-500">Pie de página (opcional)</span>
            <input maxLength={60} value={d.footer} onChange={(e) => setD({ ...d, footer: e.target.value })} className={`${inputCls} mt-1`} />
          </label>
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-foreground-500">Botones (opcional, máx. 3)</span>
              {d.buttons.length < 3 ? (
                <button className="text-xs font-semibold text-primary-700 cursor-pointer" onClick={() => setD({ ...d, buttons: [...d.buttons, { type: "QUICK_REPLY", text: "" }] })}>
                  + Añadir botón
                </button>
              ) : null}
            </div>
            {d.buttons.map((b, i) => (
              <div key={i} className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 mt-2">
                <select value={b.type} onChange={(e) => setD({ ...d, buttons: d.buttons.map((x, j) => (j === i ? { type: e.target.value as TemplateButton["type"], text: x.text } : x)) })} className={`${inputCls} !w-auto`}>
                  <option value="QUICK_REPLY">Respuesta rápida</option>
                  <option value="URL">Enlace</option>
                </select>
                <input maxLength={25} placeholder="Texto del botón" value={b.text} onChange={(e) => setD({ ...d, buttons: d.buttons.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} className={inputCls} />
                {b.type === "URL" ? (
                  <input placeholder="https://…" value={b.url ?? ""} onChange={(e) => setD({ ...d, buttons: d.buttons.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} className={inputCls} />
                ) : (
                  <span></span>
                )}
                <button className="text-foreground-500 hover:text-accent-700 cursor-pointer" aria-label="Quitar botón" onClick={() => setD({ ...d, buttons: d.buttons.filter((_, j) => j !== i) })}>
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
          </div>
          {err ? <div className="text-xs text-accent-700">{err}</div> : null}
          <div className="flex flex-wrap gap-2 pt-1">
            <button className={btnGhost} disabled={busy || !d.name || d.body.trim().length < 3} onClick={() => void save(false)}>
              Guardar borrador
            </button>
            <button className={btnPrimary} disabled={busy || !!err} onClick={() => void save(true)}>
              Guardar y enviar a Meta
            </button>
          </div>
        </div>
        <div>
          <div className="text-xs text-foreground-500 mb-2">Vista previa</div>
          <Preview d={d} />
        </div>
      </div>
    </div>
  );
}

export default function TemplatesTab({ notify }: { notify: (m: string) => void }) {
  const [list, setList] = useState<WaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await fetchTemplates());
    } catch {
      notify("No se pudieron cargar las plantillas");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const hasWebinar = useMemo(() => list.some((t) => t.name === "vivelo_webinar_bienvenida"), [list]);

  const toDraft = (t: WaTemplate): Draft => ({ id: t.id, name: t.name, language: t.language, category: t.category, headerText: t.headerText ?? "", body: t.body, footer: t.footer ?? "", buttons: t.buttons, examples: t.examples });

  if (editing) {
    return (
      <Editor
        initial={editing}
        notify={notify}
        onClose={(saved) => {
          setEditing(null);
          if (saved) void load();
        }}
      />
    );
  }

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex flex-wrap items-center gap-2">
        <button className={btnPrimary} onClick={() => setEditing({ ...EMPTY })}>
          + Nueva plantilla
        </button>
        {!hasWebinar ? (
          <button
            className={btnGhost}
            disabled={busy === "seed"}
            onClick={async () => {
              setBusy("seed");
              const r = await seedWebinarTemplates();
              setBusy(null);
              notify(r.ok ? "Plantillas del webinar creadas como borrador" : r.error ?? "No se pudieron crear");
              await load();
            }}
          >
            Crear las 2 plantillas del webinar
          </button>
        ) : null}
        <button
          className={btnGhost}
          disabled={busy === "sync"}
          onClick={async () => {
            setBusy("sync");
            const r = await syncTemplates();
            setBusy(null);
            notify(r.ok ? `Sincronizadas ${(r as { count?: number }).count ?? 0} plantillas desde Meta` : r.error ?? "No se pudo sincronizar");
            await load();
          }}
        >
          <i className="ri-refresh-line mr-1"></i>Sincronizar con Meta
        </button>
      </div>

      <div className="text-xs text-foreground-500 leading-relaxed">
        Meta revisa cada plantilla (de minutos a unas horas). Pulsa <b>Sincronizar</b> para ver si fue aprobada. Las de <b>Marketing</b> solo se pueden enviar a personas que dieron su consentimiento. No uses promesas de ingresos: Meta las rechaza.
      </div>

      {loading ? <div className="text-sm text-foreground-500">Cargando…</div> : null}
      {!loading && !list.length ? <div className={`${cardCls} text-sm text-foreground-600`}>Aún no tienes plantillas. Crea las 2 del webinar o sincroniza las que ya tengas en Meta.</div> : null}

      <div className="space-y-3">
        {list.map((t) => {
          const s = STATUS[t.status] ?? { label: t.status, cls: "bg-background-200 text-foreground-700" };
          const editable = t.status === "DRAFT" || t.status === "REJECTED";
          return (
            <div key={t.id} className={cardCls}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="font-bold text-sm text-foreground-950">{t.name}</code>
                  <span className="text-[11px] text-foreground-500">{t.language} · {CATEGORY[t.category] ?? t.category}</span>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</span>
              </div>
              <div className="mt-3">
                <Preview d={{ ...toDraft(t) }} />
              </div>
              {t.reason ? <div className="mt-2 text-xs text-accent-700">Motivo: {t.reason}</div> : null}
              <div className="mt-3 flex flex-wrap gap-2">
                {editable ? (
                  <>
                    <button className={btnGhost} onClick={() => setEditing(toDraft(t))}>
                      Editar
                    </button>
                    <button
                      className={btnPrimary}
                      disabled={busy === t.id}
                      onClick={async () => {
                        setBusy(t.id);
                        const r = await submitTemplate(t.id);
                        setBusy(null);
                        notify(r.ok ? "Enviada a revisión de Meta" : r.error ?? "No se pudo enviar");
                        await load();
                      }}
                    >
                      Enviar a revisión
                    </button>
                  </>
                ) : null}
                <button
                  className={btnDanger}
                  disabled={busy === t.id}
                  onClick={async () => {
                    if (!window.confirm(`¿Eliminar la plantilla ${t.name}? También se borra de Meta.`)) return;
                    setBusy(t.id);
                    const r = await deleteTemplate(t.id);
                    setBusy(null);
                    notify(r.ok ? "Plantilla eliminada" : r.error ?? "No se pudo eliminar");
                    await load();
                  }}
                >
                  Eliminar
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
